import React, { useMemo, useState } from "react";
import { Dropdown } from "antd";
// import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { BASE_APP_CONFIGS, AppType, BaseAppConfig } from "./baseConfig";
import { getLocale } from "src/gs-utils/core/storage/LocaleManager";
import { gsT } from "../i18n";
import { AppGrid, AppGridItem, AppGridSection } from "./AppGrid";
import logoSvg from "./images/logo.svg";
import upSvg from "./images/up.svg";
import downSvg from "./images/down.svg";
import styles from "./appGrid.module.less";

export interface HeaderApplicationGridProps {
  /** 平台名称 */
  platformName?: string;
  /** 可用的应用列表 */
  availableApps?: BaseAppConfig[];
  /** 当前应用key，用于标识当前激活的应用 */
  currentAppKey?: string;
  /** 点击应用卡片的回调 */
  onAppClick?: (app: AppGridItem, appConfig: BaseAppConfig) => void;
  /** 自定义样式类名 */
  className?: string;
  /** 下拉框可见性变化回调 */
  onVisibleChange?: (visible: boolean, ref: any) => void;
  /** 是否显示平台名称 */
  showPlatformName?: boolean;
}

/**
 * 头部应用网格组件
 * 集成了应用选择器UI和业务逻辑
 */
export const HeaderApplicationGrid: React.FC<HeaderApplicationGridProps> = ({
  currentAppKey,
  onAppClick,
  onVisibleChange,
  className = "",
  availableApps = [],
  platformName,
  showPlatformName = true,
}) => {
  const appSelectorRef = React.useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 构建应用网格数据
  const appGridSections = useMemo((): AppGridSection[] => {
    const sections: AppGridSection[] = [];
    // 根据应用类型分组
    const externalApps = availableApps.filter((app) => app.type === AppType.EXTERNAL);
    const internalApps = availableApps.filter((app) => app.type === AppType.INTERNAL);

    // 外部应用分组（我的应用）
    if (externalApps.length > 0) {
      sections.push({
        title: gsT("my_applications"),
        apps: externalApps.map((app) => ({
          name: app.name,
          icon: app.icon,
          backgroundColor: app.backgroundColor,
          current: currentAppKey === app.key,
          key: app.key,
          url: app.url,
        })),
      });
    }

    // 内部应用分组
    if (internalApps.length > 0) {
      sections.push({
        title: gsT("internal_applications"),
        apps: internalApps.map((app) => ({
          name: app.name,
          icon: app.icon,
          backgroundColor: app.backgroundColor,
          current: currentAppKey === app.key,
          key: app.key,
          url: app.url,
        })),
      });
    }

    return sections;
  }, [availableApps, currentAppKey]);

  // 处理应用点击
  const handleAppClick = (app: AppGridItem) => {
    const appConfig = availableApps.find(item => item.key === app.key);
    if (app.key === currentAppKey) {
      return;
    }
    if (appConfig && onAppClick) {
      onAppClick(app, appConfig);
    } else if (appConfig && app.url) {
      const url = new URL(app.url, window.location.origin);
      url.searchParams.set("locale", getLocale());
      window.open(url.toString(), "_blank");
    }
  };

  // 处理下拉框可见性变化
  const handleVisibleChange = (visible: boolean) => {
    setIsDropdownOpen(visible);
    onVisibleChange?.(visible, appSelectorRef);
  };

  const currentApp = availableApps.find(item => item.key === currentAppKey);
  const appGridElement = <AppGrid sections={appGridSections} onAppClick={handleAppClick} />;

  return (
    <div className={`${styles.appSelector} ${className}`}>
      {showPlatformName && (
        <div className={styles.platformName}>
          <span className={styles.logo}><img src={logoSvg} width={36} height={36} alt="logo" /></span>
          <span className={styles.name}>{platformName || gsT("gs_platform_name")}</span>
        </div>
      )}
      <Dropdown
        overlay={appGridElement}
        onVisibleChange={handleVisibleChange}
        placement="bottomLeft"
        trigger={["click"]}
      >
        <div className={styles.currentApp} ref={appSelectorRef}>
          {currentApp?.icon && (
            <div className={styles.currentAppIcon}>
              <img src={currentApp.icon} style={{ width: "20px", height: "20px" }} alt={currentApp?.name} />
            </div>
          )}
          <span className={styles.appName}>{currentApp?.name}</span>
          {isDropdownOpen ? (
            <img src={upSvg} width={20} height={20} className={styles.dropdownIcon} />
          ) : (
            <img src={downSvg} width={20} height={20} className={styles.dropdownIcon} />
          )}
        </div>
      </Dropdown>
    </div>
  );
};
